import React, { createContext, useState, Dispatch, SetStateAction, ReactNode, useEffect } from 'react';
import { useGetUserProfile } from '../services';
import { User, WebSocketHookResult } from '../@types';
import { useWebSocket } from '../hooks/websocket';
import { TaskNotifier } from '../components/notifier/TaskNotifier';
import { useDialog } from '../hooks';
import { ISocialMediaState, SocialMediaPlatform } from '../constants/social-media';
import { axiosInstance } from '../utils';

interface AppContextType {
  textToSpeechContent: string;
  setTextToSpeechContent: Dispatch<SetStateAction<string>>;
  userData: User;
  setUserData: Dispatch<SetStateAction<User | null>>;
  isUserDataLoading: boolean;
  websocket: WebSocketHookResult;
  showCreditDialog: boolean;
  setShowCreditDialog: Dispatch<SetStateAction<boolean>>;
  showAiGenModel: boolean;
  handleOpenAiGenModal: () => void;
  handleCloseAiGenModal: () => void;
  isDrawerOpen: boolean;
  setIsDrawerOpen: Dispatch<SetStateAction<boolean>>;
  socialMedia: ISocialMediaState;
}

export const AppContext = createContext<AppContextType | null>(null);

interface Props {
  children: ReactNode;
}

export const AppProvider: React.FC<Props> = ({ children }) => {
  const [textToSpeechContent, setTextToSpeechContent] = useState('');
  const [userData, setUserData] = useState<User | null>(null);
  const [showCreditDialog, setShowCreditDialog] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const { data: fetchedUserData, isLoading: isUserDataLoading } = useGetUserProfile();
  const websocket = useWebSocket(fetchedUserData?.hydraId);
  const [connectedPlatforms, setConnectedPlatforms] = useState<SocialMediaPlatform[]>([]);

  const updateConnectedPlatforms = async () => {
    if (userData) {
      try {
        const response = await axiosInstance.get('api/social-media/connected-accounts');
        const platforms = response.data.map((account: any) => account.platform);
        setConnectedPlatforms(platforms);
      } catch (error) {
        console.error('Failed to fetch connected platforms:', error);
      }
    }
  };

  useEffect(() => {
    if (fetchedUserData) {
      setUserData(fetchedUserData);
      setShowCreditDialog(fetchedUserData?.showCreditDialog);
    }
  }, [fetchedUserData]);

  useEffect(() => {
    updateConnectedPlatforms();
  }, []);

  const {
    openDialog: handleOpenAiGenModal,
    closeDialog: handleCloseAiGenModal,
    isDialogOpen: showAiGenModel,
  } = useDialog();

  return (
    <AppContext.Provider
      value={{
        textToSpeechContent,
        setTextToSpeechContent,
        userData,
        setUserData,
        isUserDataLoading,
        websocket,
        showCreditDialog,
        setShowCreditDialog,
        showAiGenModel,
        handleOpenAiGenModal,
        handleCloseAiGenModal,
        isDrawerOpen,
        setIsDrawerOpen,
        socialMedia: {
          connectedPlatforms,
          updateConnectedPlatforms,
        }
      }}
    >
      {children}
      <TaskNotifier />
    </AppContext.Provider>
  );
};
